import {CheckCircleIcon} from '@primer/octicons-react'
import {type ReactNode, useCallback, useEffect, useState} from 'react'

import {setCookie, isValidCookieValue} from '@github-ui/cookies'
import {OCTOCAPTCHA_TIMEOUT} from './config'
import {SpinnerDark, SpinnerLight} from './svgs'

export type OctocaptchaProps = {
  className?: string
  onComplete?: (options: {token: string}) => void
  onLoadError?: (error: Error) => void

  // The hostname of the octocaptcha instance to use, e.g. "octocaptcha.com"
  hostName: string

  /**
   * Octocaptcha uses the `origin_page` parameter to load the right configuration
   * (e.g. api keys, feature flags, metrics, etc.).
   */
  originPage?: string

  /**
   * The language/locale to use for the captcha widget (e.g. 'en-us', 'ja').
   */
  locale?: string

  /**
   * The number of milliseconds to wait before considering the captcha load to have timed out.
   * Defaults to 30 seconds.
   */
  timeoutAfter?: number

  /**
   * When true, the iframe loads the DataDome endpoint (/datadome) instead of the
   * Arkose endpoint (/). This enables DataDome signal collection and cookie relay.
   */
  datadomeCaptchaEnabled?: boolean

  theme?: 'light' | 'dark'
}

export function Octocaptcha(props: OctocaptchaProps) {
  // The full URL of the octocaptcha iframe (including query parameters)
  const [octocaptchaUrl, setOctocaptchaUrl] = useState<string | undefined>(undefined)

  const [isOctocaptchaReady, setOctocaptchaReady] = useState(false)
  const [isOctocaptchaSupressed, setOctocaptchaSupressed] = useState(false)
  const [hasOctocaptchaFailedToLoad, setHasOctocaptchaFailedToLoad] = useState(false)

  const [token, setToken] = useState('')

  const onCaptchaTimeout = useCallback(() => {
    if (!isOctocaptchaReady && !isOctocaptchaSupressed) {
      setHasOctocaptchaFailedToLoad(true)

      if (props.onLoadError !== undefined) {
        props.onLoadError(new Error('Octocaptcha did not load and timed out'))
      }
    }
  }, [isOctocaptchaReady, isOctocaptchaSupressed, props])

  const onMessageHandler = useCallback(
    (event: MessageEvent) => {
      const originHost = new URL(event.origin, window.location.origin).host
      if (originHost !== props.hostName) {
        return
      }

      const {data} = event

      if (data.event === 'captcha-complete') {
        setToken(data.sessionToken)

        if (props.onComplete !== undefined) {
          props.onComplete({token: data.sessionToken})
        }
      }

      if (data.event === 'captcha-loaded') {
        setOctocaptchaReady(true)
      }

      if (data.event === 'captcha-suppressed') {
        setOctocaptchaSupressed(true)
      }

      if (data.event === 'datadome-cookie') {
        const cookie = data.cookie
        if (isValidCookieValue(cookie)) {
          setCookie(
            'datadome',
            cookie,
            new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString(),
            false,
            'none',
            true,
          )
        }
      }
    },
    [props],
  )

  useEffect(() => {
    const qs = new URLSearchParams({origin: window.location.origin, responsive: 'true', require_ack: 'true'})

    if (props.originPage !== undefined) {
      qs.append('origin_page', props.originPage)
    }
    if (props.locale !== undefined) {
      qs.append('locale', props.locale)
    }

    const protocol = props.hostName.includes('localhost') ? 'http' : 'https'

    const url = new URL(`${protocol}://${props.hostName}`, window.location.origin)
    if (props.datadomeCaptchaEnabled) {
      url.pathname = '/datadome'
    }
    url.search = qs.toString()

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setOctocaptchaUrl(url.toString())
  }, [props.originPage, props.hostName, props.locale, props.datadomeCaptchaEnabled])

  useEffect(() => {
    window.addEventListener('message', onMessageHandler)

    return () => {
      window.removeEventListener('message', onMessageHandler)
    }
  }, [onMessageHandler])

  useEffect(() => {
    const timeout = setTimeout(onCaptchaTimeout, props.timeoutAfter ?? OCTOCAPTCHA_TIMEOUT)

    return () => {
      clearTimeout(timeout)
    }
  }, [props.timeoutAfter, onCaptchaTimeout])

  const Spinner = props.theme === 'dark' ? SpinnerDark : SpinnerLight

  return (
    <div
      className={`position-relative ${props.className ?? ''}`}
      style={{height: 310}}
      hidden={props.datadomeCaptchaEnabled}
    >
      {!isOctocaptchaReady && (
        <LoadingSkeleton>
          <Spinner role="img" aria-label="Loading CAPTCHA..." width={64} height={64} />
        </LoadingSkeleton>
      )}

      {isOctocaptchaSupressed && (
        <LoadingSkeleton>
          <CheckCircleIcon size={64} aria-label="Account has been verified. Please continue." />
        </LoadingSkeleton>
      )}

      {octocaptchaUrl !== undefined && (
        // eslint-disable-next-line @eslint-react/dom/no-missing-iframe-sandbox
        <iframe
          src={octocaptchaUrl}
          title="Please verify by completing this captcha."
          className="d-block position-absolute height-full width-full top-0 left-0 border-0"
          style={isOctocaptchaReady && !isOctocaptchaSupressed ? {} : {height: 0, visibility: 'hidden'}}
        />
      )}

      {hasOctocaptchaFailedToLoad ? (
        <input type="hidden" id="error_loading_captcha" name="error_loading_captcha" defaultValue="1" />
      ) : (
        <input
          className="d-none"
          name="octocaptcha-token"
          required={!props.datadomeCaptchaEnabled}
          defaultValue={token}
        />
      )}
    </div>
  )
}

const LoadingSkeleton = (props: {children: ReactNode}) => {
  return (
    <div
      className="position-absolute height-full width-full top-0 left-0 d-flex flex-justify-center flex-items-center border rounded-2"
      style={{backgroundColor: 'var(--brand-color-canvas-default)', color: 'var(--brand-color-success-fg)'}}
    >
      {props.children}
    </div>
  )
}
